import { motion } from 'framer-motion'
import { Shield, Award, Users, Lock, Crown, Globe } from 'lucide-react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const About = () => {
  const values = [
    {
      icon: Lock,
      title: 'Absolute Discretion',
      description: 'Your privacy and confidentiality are paramount in everything we do.'
    },
    {
      icon: Crown,
      title: 'White Glove Service',
      description: 'Meticulous attention to detail with service worthy of your status.'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Military-grade protection typically reserved for governments and Fortune 500 companies.'
    },
    {
      icon: Users,
      title: 'Dedicated Teams',
      description: 'Personal support from experts who know you and your technology ecosystem.'
    },
    {
      icon: Award,
      title: 'Proven Excellence',
      description: '30+ years of delivering cutting-edge technology solutions to elite clients.'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Seamless support wherever you are—from estates to yachts to private aircraft.'
    }
  ]

  return (
    <>
      <SEO 
        title="About Us"
        description="Learn about Praetorian Works, an elite technology services offering from Disruption Consulting for High Net Worth and Ultra High Net Worth clientele. White glove support and enterprise security."
        keywords="about praetorian works, HNW technology services, disruption consulting, elite IT support, luxury technology concierge"
        canonical="/about"
      />
      
      <div className="pt-20">
        {/* Hero Section */}
        <section className="section-padding bg-black/30">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl"
            >
              <Shield className="w-10 h-10 text-secondary-900" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            >
              About <span className="text-primary-500">Praetorian Works</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-neutral-300 max-w-4xl mx-auto leading-relaxed"
            >
              Elite technology services designed exclusively for High Net Worth and Ultra High Net 
              Worth individuals and families who demand excellence, discretion, and unwavering support.
            </motion.p>
          </div>
        </section>

        {/* Our Story */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <div className="max-w-4xl mx-auto space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Technology Protection <span className="text-primary-500">Reimagined</span>
                </h2>
                <p className="text-lg text-neutral-300 leading-relaxed mb-6">
                  Praetorian Works was created to address a fundamental gap in technology services: 
                  the lack of truly elite, personalized support for discerning individuals who require 
                  enterprise-grade security, white glove service, and absolute discretion.
                </p>
                <p className="text-lg text-neutral-300 leading-relaxed">
                  Drawing inspiration from the elite Praetorian Guard of ancient Rome—sworn protectors 
                  of emperors and the most powerful figures of their time—we bring that same dedication 
                  and excellence to protecting and supporting your digital life.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-white mb-4">
                  Powered by <span className="text-primary-500">Disruption Consulting</span>
                </h3>
                <p className="text-lg text-neutral-300 leading-relaxed mb-6">
                  Praetorian Works is an exclusive offering from Disruption Consulting, a technology 
                  firm with over 30 years of experience delivering cutting-edge solutions to Fortune 500 
                  companies and pioneering technology organizations worldwide.
                </p>
                <p className="text-lg text-neutral-300 leading-relaxed mb-6">
                  Since the earliest days of the Internet, Disruption Consulting has been at the forefront 
                  of technological innovation—building the infrastructure and solutions that power modern 
                  business. From Internet foundations to cloud computing, from IoT platforms to AI integration, 
                  our team has built and operated some of the world's most sophisticated technology systems.
                </p>
                <p className="text-lg text-neutral-300 leading-relaxed">
                  Now, we bring that decades of expertise directly to you—providing the same level of 
                  technology excellence that we've delivered to the world's largest enterprises, customized 
                  for your personal and family needs.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                What Defines <span className="text-primary-500">Our Service</span>
              </h2>
              <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
                The principles that guide every interaction and service we deliver.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-secondary-800 rounded-2xl p-8 border border-neutral-800"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <value.icon className="w-7 h-7 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {value.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="section-padding bg-black/30">
          <div className="container-max">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold text-white"
              >
                Why <span className="text-primary-500">Praetorian Works</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-lg text-neutral-300 leading-relaxed"
              >
                Your profile, assets, and digital footprint make you a target. Standard consumer technology 
                services are inadequate for your needs, and traditional enterprise IT is impersonal and inflexible. 
                You deserve better.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-lg text-neutral-300 leading-relaxed"
              >
                Praetorian Works delivers enterprise-class security and infrastructure with the personalized 
                service of a true concierge. We understand your unique requirements for privacy, availability, 
                and excellence. Every service is tailored to you, and every interaction reflects our commitment 
                to your complete satisfaction.
              </motion.p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-black/30">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Experience the Difference
              </h2>
              <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
                Discover how Praetorian Works can elevate your technology experience with 
                services designed exclusively for you.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Schedule Consultation
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default About
